import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { motion } from 'framer-motion';
import { Upload, X, Phone, Mail, Clock, MapPin, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { QuoteFormData } from '@/lib/index';

const quoteSchema = z.object({
  service: z.string().min(1, 'Please select a service'),
  cargoDetails: z.string().min(20, 'Please provide at least 20 characters of detail'),
  timeline: z.enum(['standard', 'urgent', 'emergency']),
  contactName: z.string().min(2, 'Name is required'),
  company: z.string().min(2, 'Company name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Valid phone number is required'),
  location: z.string().min(2, 'Location is required'),
});

type QuoteFormValues = z.infer<typeof quoteSchema>;

export default function Quote() {
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<QuoteFormValues>({
    resolver: zodResolver(quoteSchema),
    defaultValues: {
      timeline: 'standard',
    },
  });

  const selectedService = watch('service');
  const selectedTimeline = watch('timeline');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      const newFiles = Array.from(e.dataTransfer.files);
      setFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = (data: QuoteFormValues) => {
    console.log('Quote submitted:', { ...data, files });
    setIsSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const services = [
    { value: 'truck-parts', label: 'Heavy Duty Truck Parts' },
    { value: 'procurement', label: 'Procurement Services' },
    { value: 'barge-operations', label: 'Barge Operations' },
    { value: 'waste-removal', label: 'Waste Removal & Management' },
  ];

  const timelineOptions = [
    { value: 'standard', label: 'Standard', description: '5-7 business days' },
    { value: 'urgent', label: 'Urgent', description: '2-3 business days' },
    { value: 'emergency', label: 'Emergency', description: '24-48 hours' },
  ];

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background py-24">
        <div className="container mx-auto px-4 max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="border-2 border-primary/20">
              <CardHeader className="text-center pb-8">
                <div className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                </div>
                <CardTitle className="text-3xl">Quote Request Received!</CardTitle>
                <CardDescription className="text-lg mt-2">
                  Thank you for your interest in our services
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <Alert>
                  <AlertDescription className="text-base">
                    Our team will review your request and contact you within 24 hours with a detailed quote.
                    For urgent matters, please call our 24/7 hotline at <strong>+1 (242) 727-8293</strong>.
                  </AlertDescription>
                </Alert>
                <div className="bg-muted/50 rounded-lg p-6 space-y-3">
                  <h3 className="font-semibold text-lg">What happens next?</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Our logistics team will review your requirements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>We'll prepare a customized quote based on your needs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>A dedicated account manager will reach out to discuss details</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>You'll receive a comprehensive proposal via email</span>
                    </li>
                  </ul>
                </div>
                <div className="flex gap-4 pt-4">
                  <Button onClick={() => setIsSubmitted(false)} variant="outline" className="flex-1">
                    Submit Another Request
                  </Button>
                  <Button onClick={() => window.location.href = '/#/'} className="flex-1">
                    Return to Home
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-24">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Request a <span className="text-primary">Quote</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Get a customized quote for your logistics, marine, or industrial service needs.
              Our team will respond within 24 hours.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="lg:col-span-2"
            >
              <Card>
                <CardHeader>
                  <CardTitle>Quote Request Form</CardTitle>
                  <CardDescription>
                    Fill out the form below and we'll get back to you with a detailed quote
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="service">Service Required *</Label>
                        <Select
                          value={selectedService}
                          onValueChange={(value) => setValue('service', value)}
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            {services.map((service) => (
                              <SelectItem key={service.value} value={service.value}>
                                {service.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.service && (
                          <p className="text-sm text-destructive mt-1">{errors.service.message}</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="cargoDetails">Cargo/Job Details *</Label>
                        <Textarea
                          id="cargoDetails"
                          {...register('cargoDetails')}
                          placeholder="Please describe your requirements in detail (e.g., cargo type, dimensions, weight, pickup/delivery locations, special handling needs)"
                          className="mt-2 min-h-32"
                        />
                        {errors.cargoDetails && (
                          <p className="text-sm text-destructive mt-1">{errors.cargoDetails.message}</p>
                        )}
                      </div>

                      <div>
                        <Label>Upload Specifications/Photos</Label>
                        <div
                          className={`mt-2 border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                            isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                          }`}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                        >
                          <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground mb-2">
                            Drag and drop files here, or click to browse
                          </p>
                          <Input
                            type="file"
                            multiple
                            onChange={handleFileChange}
                            className="hidden"
                            id="file-upload"
                            accept="image/*,.pdf,.doc,.docx"
                          />
                          <Label htmlFor="file-upload" className="cursor-pointer">
                            <Button type="button" variant="outline" size="sm" asChild>
                              <span>Choose Files</span>
                            </Button>
                          </Label>
                        </div>
                        {files.length > 0 && (
                          <div className="mt-4 space-y-2">
                            {files.map((file, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between bg-muted/50 rounded-lg p-3"
                              >
                                <span className="text-sm truncate flex-1">{file.name}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeFile(index)}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <Label>Timeline Urgency *</Label>
                        <RadioGroup
                          value={selectedTimeline}
                          onValueChange={(value) => setValue('timeline', value as 'standard' | 'urgent' | 'emergency')}
                          className="mt-2 space-y-3"
                        >
                          {timelineOptions.map((option) => (
                            <div
                              key={option.value}
                              className="flex items-center space-x-3 border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                            >
                              <RadioGroupItem value={option.value} id={option.value} />
                              <Label htmlFor={option.value} className="flex-1 cursor-pointer">
                                <div className="font-medium">{option.label}</div>
                                <div className="text-sm text-muted-foreground">{option.description}</div>
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                        {errors.timeline && (
                          <p className="text-sm text-destructive mt-1">{errors.timeline.message}</p>
                        )}
                      </div>
                    </div>

                    <div className="border-t pt-8">
                      <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="contactName">Full Name *</Label>
                          <Input
                            id="contactName"
                            {...register('contactName')}
                            placeholder="John Smith"
                            className="mt-2"
                          />
                          {errors.contactName && (
                            <p className="text-sm text-destructive mt-1">{errors.contactName.message}</p>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="company">Company Name *</Label>
                          <Input
                            id="company"
                            {...register('company')}
                            placeholder="ABC Construction Ltd."
                            className="mt-2"
                          />
                          {errors.company && (
                            <p className="text-sm text-destructive mt-1">{errors.company.message}</p>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            type="email"
                            {...register('email')}
                            placeholder="john@company.com"
                            className="mt-2"
                          />
                          {errors.email && (
                            <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="phone">Phone Number *</Label>
                          <Input
                            id="phone"
                            {...register('phone')}
                            placeholder="+1 (242) 555-0123"
                            className="mt-2"
                          />
                          {errors.phone && (
                            <p className="text-sm text-destructive mt-1">{errors.phone.message}</p>
                          )}
                        </div>

                        <div className="md:col-span-2">
                          <Label htmlFor="location">Service Location *</Label>
                          <Input
                            id="location"
                            {...register('location')}
                            placeholder="Nassau, New Providence"
                            className="mt-2"
                          />
                          {errors.location && (
                            <p className="text-sm text-destructive mt-1">{errors.location.message}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <Button type="submit" size="lg" className="w-full">
                      Submit Quote Request
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="space-y-6"
            >
              <Card>
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium">24/7 Hotline</p>
                      <p className="text-sm text-muted-foreground">+1 (242) 727-8293</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium">Email</p>
                      <p className="text-sm text-muted-foreground">quotes@clhservices.bs</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium">Head Office</p>
                      <p className="text-sm text-muted-foreground">
                        123 Bay Street<br />
                        Nassau, New Providence<br />
                        The Bahamas
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Business Hours</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-primary" />
                    <div className="flex-1">
                      <p className="font-medium">Monday - Friday</p>
                      <p className="text-sm text-muted-foreground">8:00 AM - 6:00 PM</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-primary" />
                    <div className="flex-1">
                      <p className="font-medium">Saturday</p>
                      <p className="text-sm text-muted-foreground">9:00 AM - 2:00 PM</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-primary" />
                    <div className="flex-1">
                      <p className="font-medium">Sunday</p>
                      <p className="text-sm text-muted-foreground">Closed</p>
                    </div>
                  </div>
                  <div className="pt-3 border-t">
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Emergency Services:</strong> Available 24/7 for urgent logistics needs
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">
                    <strong className="text-foreground">Need immediate assistance?</strong><br />
                    For urgent quotes or emergency services, call our 24/7 hotline. Our team is ready to help with time-sensitive logistics needs.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}